// Compact "Commission" panel on the sales order page (CEO 2026-10-15).
// Finance / admin only, only once compensation plans are switched on, and
// only from the committed stage on. Everything else lives in
// Finance → Commissions → Orders (linked).
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";
import { type OrderDetail, errReason, fmtDay, isFinanceRole, usePlanStatus } from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";

const STAGES = new Set(["committed", "in_production", "shipped", "invoiced", "closed"]);

export function OrderCommissionPanel({ orderId, stage }: { orderId: string; stage: string }) {
  const { user } = useAuth();
  const show = isFinanceRole(user?.roles) && STAGES.has(stage);
  const status = usePlanStatus(show);
  const ready = show && status.data?.ready === true;
  if (!ready) return null;
  return <PanelBody orderId={orderId} />;
}

function PanelBody({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["commissions", "order", orderId],
    queryFn: () => api<OrderDetail>(`/commissions/orders/${orderId}`),
  });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const finalize = useMutation({
    mutationFn: () => api(`/orders/${orderId}/finalize-costs`, { method: "POST" }),
    onSuccess: () => {
      setMsg({ ok: true, text: "Costs finalized." });
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => setMsg({ ok: false, text: errReason(e) }),
  });

  const d = q.data;
  const a = d?.allocation;
  const orderRepName = a?.orderRepId
    ? d?.commissions.find((c) => c.rep_id === a.orderRepId)?.rep_name ??
      (d?.order.order_rep_id === a.orderRepId ? d?.order.order_rep_name : null) ??
      "the Order Rep"
    : null;

  return (
    <section className="mb-4 rounded-md border border-rule bg-white px-5 py-4" aria-label="Commission">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[16px] font-semibold text-ink">Commission</h3>
        <Link
          to={`/finance/commissions?tab=orders&order=${orderId}`}
          className="text-[14px] font-medium text-ink underline underline-offset-2"
        >
          Open in Finance → Commissions
        </Link>
      </div>
      {q.isError ? (
        <p className="mt-2 text-[15px] text-risk">{errReason(q.error)}</p>
      ) : !d ? (
        <p className="mt-2 text-[15px] text-muted">Loading…</p>
      ) : (
        <div className="mt-2 flex flex-wrap items-center gap-x-8 gap-y-2 text-[15px] text-ink">
          <span>
            {d.eligibility?.eligible
              ? `Eligible since ${fmtDay(d.eligibility.eligibleAt)}`
              : `Not eligible yet: ${(d.eligibility?.reasonText ?? []).join(", ") || "—"}`}
          </span>
          <span>
            Split:{" "}
            {a && a.preset !== "100/0" ? `${a.preset} with ${orderRepName}` : "none — Client Rep keeps 100%"}
          </span>
          {d.order.costs_finalized_at ? (
            <span className="text-muted">Costs finalized {fmtDay(d.order.costs_finalized_at)}</span>
          ) : (
            <Button size="sm" onClick={() => finalize.mutate()} disabled={finalize.isPending}>
              {finalize.isPending ? "Finalizing…" : "Finalize costs"}
            </Button>
          )}
          {d.commissions.some((c) => c.needs_review) && <span className="font-medium text-warn">Needs Finance review</span>}
        </div>
      )}
      {msg && <p className={`mt-2 text-[15px] ${msg.ok ? "text-ok" : "text-risk"}`}>{msg.text}</p>}
    </section>
  );
}
